// Phase 15 — Web API client for the marketplace SPA.
//
// Pre-phase-15 this file used a standalone axios instance with cookie-based
// auth. Phase 15 M1 unified everything onto the Bearer-token shared client
// (`./client`), so we never juggle two auth modes. The public catalog
// (search / sales/:id for LISTED) still works anonymously: the request
// interceptor only attaches Authorization when an access token is present.

import {
  API_VERSION_PREFIX,
  type BuyerConfirmRequest,
  type BuyerConfirmResponse,
  type CheckoutIntentResponse,
  type DisputeRequest,
  type MarketplaceSaleDetail,
  type MarketplaceSaleListResponse,
  type MarketplaceSearchResponse,
  type PaymentStatusResponse,
  type ReserveSaleRequest,
  type SigningUrlResponse,
} from '@leyrochain/shared';

import { apiClient } from './client';

export interface SearchParams {
  q?: string;
  breed?: string;
  destination?: 'BEEF' | 'DAIRY' | 'BREEDING';
  verifiedOnly?: boolean;
  page?: number;
  limit?: number;
}

export async function searchMarketplace(
  params: SearchParams = {},
): Promise<MarketplaceSearchResponse> {
  const { data } = await apiClient.get<MarketplaceSearchResponse>(
    `${API_VERSION_PREFIX}/marketplace/search`,
    {
      params: {
        q: params.q || undefined,
        breed: params.breed || undefined,
        destination: params.destination || undefined,
        verified_only: params.verifiedOnly ? true : undefined,
        page: params.page ?? 1,
        limit: params.limit ?? 24,
      },
    },
  );
  return data;
}

export async function getSale(saleId: string): Promise<MarketplaceSaleDetail> {
  const { data } = await apiClient.get<MarketplaceSaleDetail>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}`,
  );
  return data;
}

export async function reserveSale(
  saleId: string,
  input: ReserveSaleRequest = {},
): Promise<CheckoutIntentResponse> {
  const { data } = await apiClient.post<CheckoutIntentResponse>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}/reserve`,
    input,
  );
  return data;
}

export async function confirmBuyerSale(
  saleId: string,
  input: BuyerConfirmRequest,
): Promise<BuyerConfirmResponse> {
  const { data } = await apiClient.post<BuyerConfirmResponse>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}/buyer-confirm`,
    input,
  );
  return data;
}

/**
 * Lote V-3 (F-030) — fetch the real Stripe PaymentIntent status for a
 * sale. Used by CheckoutPage's post-payment branch to decide whether
 * to render the "✓ Pagamento procesado" panel or surface a "payment
 * not completed" error (the URL `?payment=ok` alone is not authoritative).
 */
export async function getPaymentStatus(saleId: string): Promise<PaymentStatusResponse> {
  const { data } = await apiClient.get<PaymentStatusResponse>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}/payment-status`,
  );
  return data;
}

export async function getSigningUrl(saleId: string): Promise<SigningUrlResponse> {
  const { data } = await apiClient.get<SigningUrlResponse>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}/signing-url`,
  );
  return data;
}

export async function markDelivered(saleId: string): Promise<MarketplaceSaleDetail> {
  const { data } = await apiClient.post<MarketplaceSaleDetail>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}/delivered`,
  );
  return data;
}

export async function openDispute(
  saleId: string,
  input: DisputeRequest,
): Promise<MarketplaceSaleDetail> {
  const { data } = await apiClient.post<MarketplaceSaleDetail>(
    `${API_VERSION_PREFIX}/marketplace/sales/${saleId}/dispute`,
    input,
  );
  return data;
}

export async function salesMineBuyer(): Promise<MarketplaceSaleListResponse> {
  const { data } = await apiClient.get<MarketplaceSaleListResponse>(
    `${API_VERSION_PREFIX}/marketplace/sales/me/buyer`,
  );
  return data;
}
